import type { UpsertFileParams, UpdateFileAttachmentMetadataParams } from '../api';
import { useState } from 'react';
import { calculateFileMetadata } from '~/utils';
import { useUploadFileActiveStorage } from '~/hooks';
import { CMS_RETRY_CONFIG } from '../constants';
import { useUpsertFile, useUpdateFileAttachmentMetadata } from '../api';

export type FileUploaderParams = {
  onUploadSuccess: ({ assetId }: { assetId: string }) => void;
  onUploadFailed: () => void;
  afterUpload: () => void;
};

/**
 * Uploading CMS files is a multistep process:
 * 1. Upsert the file (creates a new one if it does not exist) and generate a pre-signed URL via the CMS API
 *    - This API call creates the TopLevelContent, CMSFile, and ActiveStorage::Blob records if they do not exist
 * 2. Upload the file to S3 directly via the pre-signed URL
 * 3. Update the CMSFile's attachment metadata via the CMS API
 *    - Note, additional metadata is updated when an existing file is replaced
 */
export const useFileUploader = ({ onUploadSuccess, onUploadFailed, afterUpload }: FileUploaderParams) => {
  const upsertFile = useUpsertFile(CMS_RETRY_CONFIG);
  const uploadFileActiveStorage = useUploadFileActiveStorage(CMS_RETRY_CONFIG);
  const updateFileAttachmentMetadata = useUpdateFileAttachmentMetadata(CMS_RETRY_CONFIG);

  const [isUploading, setIsUploading] = useState(false);

  const uploadFile = async (file: File, existingFileId: string = null) => {
    setIsUploading(true);

    const { name, size, type, checksum } = await calculateFileMetadata(file);
    const input: UpsertFileParams = {
      fileName: name,
      fileSize: size,
      fileType: type,
      fileChecksum: checksum,
    };

    // Send the file id to upsert api if we are replacing an existing file's attachment
    if (existingFileId && existingFileId.length > 0) {
      input.id = existingFileId;
    }

    upsertFile.mutate(input, {
      onSuccess: async (data) => {
        const { activeStorageBlob } = data;
        const uploadFileInput = { file, url: activeStorageBlob.url, headers: activeStorageBlob.headers };

        uploadFileActiveStorage.mutate(uploadFileInput, {
          onSuccess: async () => {
            const newMetadata: UpdateFileAttachmentMetadataParams = {
              signedId: activeStorageBlob.signedId,
              id: data.assetId,
              isUploaded: true,
            };
            // Send an isFileReplaced flag and new metadata if we are replacing an existing file's attachment
            if (existingFileId && existingFileId.length > 0) {
              newMetadata.isFileReplaced = true;
              newMetadata.fileName = name;
              newMetadata.fileType = type;
            }

            updateFileAttachmentMetadata.mutate(newMetadata, {
              onSuccess: () => {
                setIsUploading(false);
                onUploadSuccess({ assetId: data.assetId });
                afterUpload();
              },
              onError: () => {
                setIsUploading(false);
                onUploadFailed();
              },
            });
          },
          onError: () => {
            updateFileAttachmentMetadata.mutate(
              {
                signedId: activeStorageBlob.signedId,
                id: data.assetId,
                isUploaded: false,
              },
              {
                onSettled: () => {
                  setIsUploading(false);
                  onUploadFailed();
                },
              },
            );
          },
        });
      },
      onError: () => {
        setIsUploading(false);
        onUploadFailed();
      },
    });
  };

  return { isUploading, uploadFile };
};
